// Payment System v3.0 (WS2) — staff "Record payoff" action. The orchestrator
// computes the full settlement internally (arrears + fees + 3% prepayment
// admin fee + YMP), returns the remaining principal to the seller, and retires
// the loan; staff only confirm the effective date. The borrower-facing payoff
// quote / statement is out of scope.

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Button, Input, Label } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { usePayoff } from "@/hooks/portfolioAdmin/usePayoff";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

export function PayoffModal({
  loanId,
  open,
  onClose,
  initialDate,
}: {
  loanId: string;
  open: boolean;
  onClose: () => void;
  /** Transitions PR-3 — prefill (e.g. a scheduled transition's tender date,
   *  "yyyy-MM-dd"). Applied each time the modal opens. */
  initialDate?: string;
}) {
  const mutation = usePayoff(loanId);
  const tz = useUserTimeZone();
  const [payoffDate, setPayoffDate] = useState<string>(
    () => isoDateInTz(new Date(), tz),
  );
  useEffect(() => {
    if (open && initialDate) setPayoffDate(initialDate);
  }, [open, initialDate]);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mutation.mutateAsync({
        payoffDate: payoffDate || null,
        externalReference: reference.trim() || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "The payoff could not be recorded.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record payoff"
      description="Settles all outstanding arrears and fees, the 3% prepayment admin fee and the yield-maintenance premium, returns the remaining principal to the seller, and retires the loan as paid in full. This cannot be undone here."
    >
      <form onSubmit={submit} className="flex flex-col gap-space-3" data-testid="payoff-form">
        <div>
          <Label htmlFor="payoff-date">Payoff date</Label>
          <Input
            id="payoff-date"
            type="date"
            required
            value={payoffDate}
            onChange={(e) => setPayoffDate(e.target.value)}
            data-testid="payoff-date"
          />
        </div>
        <div>
          <Label htmlFor="payoff-reference">Servicer reference</Label>
          <Input
            id="payoff-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Optional — FCI transaction ref"
            data-testid="payoff-reference"
          />
        </div>
        <div>
          <Label htmlFor="payoff-notes">Notes</Label>
          <Input
            id="payoff-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </div>
        {error && (
          <p className="text-small text-status-error m-0" role="alert" data-testid="payoff-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isPending} data-testid="payoff-submit">
            {mutation.isPending ? "Recording…" : "Record payoff"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
